/**
 * PATH: packages/ui/src/components/sidebar/buildNavContextMenu.ts
 * PURPOSE: Pure I→O builder for the right-click context menu on a nav
 *          item in the shared `@quotech/ui` Sidebar.
 * WHY: One canonical menu shape so every hub that consumes the shared
 *      Sidebar surfaces the same affordances (Pin/Unpin, Move Up/Down,
 *      Customise…, Open in new tab) without duplicating string + icon
 *      choices. Mirrors the in-app pattern that SAM and Newmark
 *      already use for their custom shells (see
 *      `apps/sam/src/shell/Sidebar.tsx`).
 *
 * Pure function — no side effects, no hooks, no `window` access. All
 * actions are passed in as callbacks so consumers wire them to
 * whatever store/router/API mutation makes sense. That keeps the
 * builder unit-testable without a DOM, and means we can render the
 * same menu inside Storybook with `noop` actions.
 */

import {
  ExternalLink, Pin, PinOff, Settings2, ArrowUp, ArrowDown, Keyboard, EyeOff, Eye,
} from 'lucide-react'
import type { ContextMenuItem } from '../../contextMenu/types'
import type { NavItem } from '../types'

export interface NavContextMenuActions {
  /** Pin this nav item to the "Pinned" section above the main nav. */
  onPin?: () => void
  /** Remove this item from the pinned section. */
  onUnpin?: () => void
  /** Move the item one slot up in its section's order. */
  onMoveUp?: () => void
  /** Move the item one slot down in its section's order. */
  onMoveDown?: () => void
  /** Hide this item from the rail (it moves to the "Hidden" restore tray). */
  onHide?: () => void
  /** Restore a hidden item back into the rail. */
  onUnhide?: () => void
  /** Open the full NavigationCustomization modal (drag-and-drop, presets). */
  onOpenCustomize?: () => void
  /** Open the item's href in a new browser tab (only meaningful when the item has an href). */
  onOpenInNewTab?: () => void
  /**
   * Open the key-capture dialog to set / change / remove a ⌘/Ctrl+<key>
   * shortcut that jumps to this hub. When omitted the row is suppressed.
   */
  onSetShortcut?: () => void
}

export interface NavContextMenuFlags {
  /** Whether the item is currently pinned (drives Pin vs Unpin label + icon). */
  isPinned: boolean
  /** Whether the item is currently hidden (drives Hide vs Show label + icon). */
  isHidden?: boolean
  /** Whether "Move Up" should be enabled — false at the top of the section. */
  canMoveUp: boolean
  /** Whether "Move Down" should be enabled — false at the bottom of the section. */
  canMoveDown: boolean
  /**
   * Pre-formatted display of the item's current shortcut (e.g. `⌘E`),
   * shown as a hint on the "Keyboard shortcut" row. Undefined ⇒ no shortcut
   * yet, so the row reads "Set…".
   */
  shortcut?: string
}

/**
 * Build the declarative menu shape for one nav item.
 *
 * Layout (top → bottom):
 *   • header (label of the nav item, non-interactive)
 *   • Pin / Unpin (mutually exclusive — `isPinned` decides which renders)
 *   • Hide / Show (mutually exclusive — `isHidden` decides which renders)
 *   • Move Up   (disabled when `canMoveUp === false`)
 *   • Move Down (disabled when `canMoveDown === false`)
 *   • Customise…  (opens the full modal)
 *   • Open in new browser tab  (only rendered when the item has an href)
 *
 * Any action that's missing (e.g. caller doesn't pass `onPin`) is
 * omitted from the menu — that means a Storybook "read-only" story
 * can simply pass an empty `actions` object and render a tidy menu
 * with only the header.
 */
export function buildNavContextMenu(
  item: NavItem,
  flags: NavContextMenuFlags,
  actions: NavContextMenuActions,
): ContextMenuItem[] {
  const out: ContextMenuItem[] = []

  // Header — the item's label, visually distinct from selectable rows
  // so users always see which item the menu refers to. `kind: 'header'`
  // is rendered by the shared `<ContextMenu>` primitive as a small
  // uppercase caption row.
  out.push({
    id: 'nav-ctx-header',
    kind: 'header',
    label: item.label,
  })

  // Pin / Unpin — mutually exclusive. When the user has no
  // pin/unpin handler wired (e.g. inside a Storybook read-only
  // sample), we suppress the row entirely rather than rendering a
  // disabled affordance with no behaviour.
  if (flags.isPinned && actions.onUnpin) {
    out.push({
      id: 'nav-ctx-unpin',
      label: 'Unpin from sidebar',
      icon: PinOff,
      onSelect: actions.onUnpin,
    })
  } else if (!flags.isPinned && actions.onPin) {
    out.push({
      id: 'nav-ctx-pin',
      label: 'Pin to sidebar',
      icon: Pin,
      onSelect: actions.onPin,
    })
  }

  // Hide / Show — mutually exclusive, the "menu building" affordance.
  // "Hide from sidebar" tucks the item into the bottom "Hidden (N)"
  // restore tray; "Show in sidebar" brings it back. As with Pin, a
  // missing handler suppresses the row (read-only / not-opted-in hosts).
  if (flags.isHidden && actions.onUnhide) {
    out.push({
      id: 'nav-ctx-unhide',
      label: 'Show in sidebar',
      icon: Eye,
      onSelect: actions.onUnhide,
    })
  } else if (!flags.isHidden && actions.onHide) {
    out.push({
      id: 'nav-ctx-hide',
      label: 'Hide from sidebar',
      icon: EyeOff,
      onSelect: actions.onHide,
    })
  }

  // Move Up / Move Down — disabled (greyed) at the boundaries rather
  // than hidden, so the affordance is discoverable. We deliberately
  // render BOTH rows even when the item is at the top/bottom — the
  // disabled state telegraphs "this exists, you've just hit the edge"
  // rather than the menu mysteriously losing rows depending on
  // position.
  if (actions.onMoveUp) {
    out.push({
      id: 'nav-ctx-move-up',
      label: 'Move up',
      icon: ArrowUp,
      onSelect: actions.onMoveUp,
      disabled: !flags.canMoveUp,
    })
  }
  if (actions.onMoveDown) {
    out.push({
      id: 'nav-ctx-move-down',
      label: 'Move down',
      icon: ArrowDown,
      onSelect: actions.onMoveDown,
      disabled: !flags.canMoveDown,
    })
  }

  // Separator only if we have at least one row above AND at least one
  // row below — avoids leading/trailing rules.
  const hasUpperBlock =
    actions.onPin || actions.onUnpin || actions.onHide || actions.onUnhide ||
    actions.onMoveUp || actions.onMoveDown
  const hasLowerBlock =
    actions.onSetShortcut || actions.onOpenCustomize || (actions.onOpenInNewTab && item.href)
  if (hasUpperBlock && hasLowerBlock) {
    out.push({ id: 'nav-ctx-sep', kind: 'separator' })
  }

  // "Set keyboard shortcut" — jump to this hub with ⌘/Ctrl+<key>. The hint
  // shows the current chord (e.g. ⌘E) so the user sees it's already bound,
  // or "Set…" when unbound.
  if (actions.onSetShortcut) {
    out.push({
      id: 'nav-ctx-shortcut',
      label: 'Keyboard shortcut',
      icon: Keyboard,
      hint: flags.shortcut ?? 'Set…',
      onSelect: actions.onSetShortcut,
    })
  }

  if (actions.onOpenCustomize) {
    out.push({
      id: 'nav-ctx-customise',
      label: 'Customise sidebar…',
      icon: Settings2,
      hint: 'All options',
      onSelect: actions.onOpenCustomize,
    })
  }

  // "Open in new tab" only when the item actually has an href. For
  // pure action items (item.action with no href) or section
  // dividers, opening a tab makes no sense.
  if (actions.onOpenInNewTab && item.href) {
    out.push({
      id: 'nav-ctx-open-tab',
      label: 'Open in new browser tab',
      icon: ExternalLink,
      onSelect: actions.onOpenInNewTab,
    })
  }

  return out
}
